/**
 * Audio Device Validation Utilities
 * 
 * Provides utility functions for validating audio devices, tracks, and streams.
 * Used by Test.js and rec.jsx to ensure audio capture is working correctly.
 */

/**
 * Measure actual audio amplitude from analyser
 * 
 * @param {AnalyserNode} analyser - Web Audio API analyser node
 * @param {Uint8Array} dataArray - Frequency data array
 * @returns {number} Audio level (0-10 scale)
 */
export function getAudioLevel(analyser, dataArray) {
  if (!analyser || !dataArray) {
    return 0;
  }

  analyser.getByteFrequencyData(dataArray);
  const sum = dataArray.reduce((p, c) => p + c, 0);
  const audioLevel = (Math.sqrt(sum / dataArray.length) * 10) | 0;
  
  return audioLevel;
}

/**
 * Validate that an audio track exists, is enabled, and in "live" state
 * 
 * @param {MediaStreamAudioTrack} track - Audio track to validate
 * @returns {boolean} True if track is valid, false otherwise
 */
export function validateAudioTrack(track) {
  if (!track) {
    return false;
  }

  // Check if track exists and is enabled
  if (!track.enabled) {
    return false;
  }

  // Check if track is muted
  if (track.muted) {
    return false;
  }

  // Check if track is in "live" state
  if (track.readyState !== 'live') {
    return false;
  }

  return true;
}

/**
 * Get a valid audio device ID
 * 
 * Enumerates audio devices and validates the preferred device.
 * Falls back to default device or next available device if preferred is invalid.
 * 
 * @param {string} preferredDeviceId - Preferred device ID (optional)
 * @returns {Promise<string|null>} Valid device ID or null if no device available
 */
export async function getValidAudioDevice(preferredDeviceId) {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioDevices = devices.filter((d) => d.kind === 'audioinput');

    if (audioDevices.length === 0) {
      return null;
    }

    // If preferred device is specified, check if it exists and is valid
    if (preferredDeviceId) {
      const preferredDevice = audioDevices.find((d) => d.deviceId === preferredDeviceId);
      if (preferredDevice) {
        // Verify the device has audio capability
        const hasAudio = await hasAudioCapability(preferredDeviceId);
        if (hasAudio) {
          return preferredDeviceId;
        }
      }
    }

    // Fall back to default device (empty string or first device)
    const defaultDevice = audioDevices.find((d) => d.deviceId === 'default' || d.deviceId === '');
    if (defaultDevice) {
      const hasAudio = await hasAudioCapability(defaultDevice.deviceId);
      if (hasAudio) {
        return defaultDevice.deviceId;
      }
    }

    // Try next available device
    for (const device of audioDevices) {
      const hasAudio = await hasAudioCapability(device.deviceId);
      if (hasAudio) {
        return device.deviceId;
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting valid audio device:', error);
    return null;
  }
}

/**
 * Check if a device has audio capability
 * 
 * Attempts to create a stream with the device ID and checks for audio tracks.
 * Cleans up the stream after checking.
 * 
 * @param {string} deviceId - Device ID to check
 * @returns {Promise<boolean>} True if device has audio capability, false otherwise
 */
export async function hasAudioCapability(deviceId) {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: deviceId ? { exact: deviceId } : undefined },
      video: false,
    });

    const audioTracks = stream.getTracks();
    const hasAudio = audioTracks.length > 0;

    // Clean up stream
    audioTracks.forEach((track) => track.stop());

    return hasAudio;
  } catch (error) {
    console.error(`Device ${deviceId} does not have audio capability:`, error);
    return false;
  }
}

/**
 * Monitor audio track state changes
 * 
 * Sets up listeners for track state changes (enabled, muted, ended).
 * Calls callback when track becomes invalid.
 * 
 * @param {MediaStreamAudioTrack} track - Audio track to monitor
 * @param {Function} onStateChange - Callback when track state changes
 * @returns {Function} Cleanup function to remove listeners
 */
export function monitorTrackState(track, onStateChange) {
  if (!track) {
    return () => {};
  }

  const handleEnded = () => {
    onStateChange({ type: 'ended', track });
  };

  const handleMute = () => {
    onStateChange({ type: 'mute', track });
  };

  const handleUnmute = () => {
    onStateChange({ type: 'unmute', track });
  };

  // Listen for track state changes
  track.addEventListener('ended', handleEnded);
  track.addEventListener('mute', handleMute);
  track.addEventListener('unmute', handleUnmute);

  // Return cleanup function
  return () => {
    track.removeEventListener('ended', handleEnded);
    track.removeEventListener('mute', handleMute);
    track.removeEventListener('unmute', handleUnmute);
  };
}
